import PropTypes from 'prop-types';
import React from 'react';
import { createPortal } from 'react-dom';
import Tooltip from 'root/components/molecules/Tooltip/tooltip';

const modalRoot = document.getElementById('modals');

/*
  Usage:

  <Tooltip
    active={active}
    parent={ref}
    position='left'
  >
    {Text}
  </Tooltip>

  Props:

  - active: boolean, the tooltip will be visible if true
  - className: string, CSS class to apply to the tooltip
  - offset: number, how far the tooltip gets placed from the parent
  - parent: the tooltip will be placed next to this element, must be a React ref
  - position: top, right, bottom or left. Default to left.
  - variant: default, arrow
*/

const TooltipPortal = ({
  active,
  children,
  className,
  offset,
  parent,
  position,
  variant,
  maxWidth,
  onMouseEnter,
  onMouseLeave,
}) => {
  if (!active || !parent) return null;

  return createPortal(
    <Tooltip
      active={active}
      className={className}
      maxWidth={maxWidth}
      offset={offset}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      parent={parent}
      position={position}
      variant={variant}
    >
      {children}
    </Tooltip>,
    modalRoot,
  );
};

TooltipPortal.propTypes = {
  active: PropTypes.bool,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  maxWidth: PropTypes.number,
  offset: PropTypes.number,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  parent: PropTypes.oneOfType([
    // Either a function
    PropTypes.func,
    // Or the instance of a DOM native element (see the note about SSR)
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]).isRequired,
  position: PropTypes.string,
  variant: PropTypes.oneOf(['default', 'arrow']),
};

TooltipPortal.defaultProps = {
  active: false,
  className: '',
  maxWidth: 0,
  offset: 10,
  onMouseEnter: undefined,
  onMouseLeave: undefined,
  position: 'left',
  variant: 'default',
};

export default TooltipPortal;
